import { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  Paperclip,
  RefreshCw,
  Pencil,
} from 'lucide-react';
import { nanoid } from 'nanoid';
import { useVault } from '../../../core/lib/vault';
import {
  listDecryptedDocuments,
  upsertDocument,
  deleteDocument,
  subscribe,
  getDecryptedDocument,
  type DecryptedDocument,
} from '../sync';
import { DEFAULT_APP_ICON, getAppIcon, isAppIconKey } from '../lib/appIcons';
import { api } from '../lib/api';
import { useLiveMeta, updateLiveMeta } from '../lib/yjsCollab';
import { useCanEdit } from './../lib/usePermissions';
import {
  ATTACHMENT_DRAG_MIME,
  DOC_UPDATED_EVENT,
  buildAttachmentRef,
  type AttachmentDragPayload,
} from '../lib/attachmentTypes';
import { dragHasFiles, uploadFiles } from '../lib/attachmentUpload';
import { useBreakpoint } from '../../../core/lib/useBreakpoint';
import { ResizableAside } from './ResizableAside';

/** Sliding FIFO size — match CustomerPane so the UX is consistent. */
const EXCLUSION_WINDOW = 5;

export function DocumentPane({
  customerId,
  selectedDocId,
  onSelect,
  onOpenAttachments,
}: {
  customerId: string | null;
  selectedDocId: string | null;
  onSelect: (id: string) => void;
  onOpenAttachments?: () => void;
}) {
  const vaultKey = useVault((s) => s.vaultKey);
  // Editor-or-above can mutate the page list; viewers get a read-only
  // rendering (no New/Rename/Delete affordances). Server gates the same way.
  const canEdit = useCanEdit();
  const breakpoint = useBreakpoint();
  const [docs, setDocs] = useState<DecryptedDocument[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftIcon, setDraftIcon] = useState<string>(DEFAULT_APP_ICON);
  // Set true when the user types an explicit icon key via the escape-hatch
  // input. Blocks the save-time auto-suggester from overwriting their pick.
  const [iconManuallyPicked, setIconManuallyPicked] = useState(false);
  // Title at edit-start — a suggest is only worth re-running if the title
  // actually changed, or the icon is still at the default.
  const [editBaselineTitle, setEditBaselineTitle] = useState('');
  // Regenerate cycle state: FIFO of recent icons the model should avoid.
  const [exclusionHistory, setExclusionHistory] = useState<string[]>([]);
  const [regenerating, setRegenerating] = useState(false);
  // Power-user escape hatch: toggles a tiny text input for explicit key entry.
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [dropError, setDropError] = useState<string | null>(null);

  function pushExclusion(icon: string | undefined) {
    if (!icon) return;
    setExclusionHistory((prev) =>
      [icon, ...prev.filter((k) => k !== icon)].slice(0, EXCLUSION_WINDOW),
    );
  }

  function resetEditSessionState() {
    setExclusionHistory([]);
    setIconManuallyPicked(false);
    setShowKeyInput(false);
    setKeyInput('');
    setRegenerating(false);
  }

  async function reload() {
    if (!vaultKey || !customerId) {
      setDocs([]);
      return;
    }
    const list = await listDecryptedDocuments(customerId, vaultKey);
    // Show only top-level pages here; sub-pages appear as tabs in the editor pane.
    setDocs(list.filter((d) => !d.parentId));
  }

  useEffect(() => {
    reload();
    return subscribe(reload);
  }, [vaultKey, customerId]);

  async function addNew() {
    if (!vaultKey || !customerId) return;
    const id = nanoid();
    await upsertDocument(
      id,
      customerId,
      null,
      { title: 'Untitled Page', markdown: '', icon: DEFAULT_APP_ICON },
      vaultKey,
      true,
    );
    onSelect(id);
    setEditing(id);
    setDraftTitle('Untitled Page');
    setDraftIcon(DEFAULT_APP_ICON);
    setEditBaselineTitle('Untitled Page');
    resetEditSessionState();
  }

  async function regenerateIcon() {
    if (!vaultKey || regenerating) return;
    const title = draftTitle.trim();
    if (!title) return;
    setRegenerating(true);
    try {
      const exclude = draftIcon
        ? [draftIcon, ...exclusionHistory.filter((k) => k !== draftIcon)].slice(
            0,
            EXCLUSION_WINDOW,
          )
        : exclusionHistory;
      const res = await api.suggestIcon({
        name: title,
        kind: 'page',
        excludeIcons: exclude,
      });
      if (res.icon) {
        pushExclusion(draftIcon);
        setDraftIcon(res.icon);
      }
    } catch {
      /* swallow */
    } finally {
      setRegenerating(false);
    }
  }

  function applyKeyInput() {
    const candidate = keyInput.trim().toLowerCase();
    if (isAppIconKey(candidate)) {
      setDraftIcon(candidate);
      setIconManuallyPicked(true);
      setShowKeyInput(false);
      setKeyInput('');
    }
  }

  async function saveEdit(d: DecryptedDocument) {
    if (!vaultKey) return;
    const nextTitle = draftTitle.trim() || d.title;
    // Order matters: updateLiveMeta first so the Y.Doc's meta map reflects
    // the rename BEFORE upsertDocument runs, because upsertDocument merges
    // the live Y.Doc body (including meta) into the outgoing payload. If
    // we flipped the order, the server would get `plain.title = new` but
    // `yjsState.meta.title = old`, and the next persistSnapshot would then
    // stomp the server with the stale meta.
    updateLiveMeta(d.id, { title: nextTitle, icon: draftIcon });
    await upsertDocument(
      d.id,
      d.customerId,
      d.parentId,
      {
        title: nextTitle,
        markdown: d.markdown,
        tags: d.tags,
        icon: draftIcon,
        color: d.color,
      },
      vaultKey,
      false,
    );
    setEditing(null);

    // Auto-suggest via Haiku when the user hasn't pinned an explicit key this
    // session AND the title changed OR the icon is still the default.
    // Fire-and-forget — any failure silently leaves the existing icon.
    const titleChanged = nextTitle !== editBaselineTitle;
    const shouldSuggest =
      !iconManuallyPicked &&
      nextTitle.length > 0 &&
      (titleChanged || draftIcon === DEFAULT_APP_ICON);
    if (shouldSuggest) {
      try {
        const res = await api.suggestIcon({
          name: nextTitle,
          kind: 'page',
          excludeIcons: exclusionHistory,
        });
        if (res.icon && res.icon !== draftIcon) {
          // updateLiveMeta before upsertDocument — see note in saveEdit above.
          updateLiveMeta(d.id, { icon: res.icon });
          await upsertDocument(
            d.id,
            d.customerId,
            d.parentId,
            {
              title: nextTitle,
              markdown: d.markdown,
              tags: d.tags,
              icon: res.icon,
              color: d.color,
            },
            vaultKey,
            false,
          );
        }
      } catch {
        /* swallow */
      }
    }
  }

  async function remove(id: string) {
    if (!confirm('Move this page to the Orphanage?')) return;
    await deleteDocument(id);
  }

  async function handleDrop(target: DecryptedDocument, e: React.DragEvent) {
    e.preventDefault();
    setDropTarget(null);
    if (!vaultKey) return;

    let payloads: AttachmentDragPayload[] = [];
    const raw = e.dataTransfer.getData(ATTACHMENT_DRAG_MIME);
    if (raw) {
      try {
        payloads = [JSON.parse(raw)];
      } catch {
        /* fall through */
      }
    } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      try {
        payloads = await uploadFiles(e.dataTransfer.files, target.customerId, vaultKey);
      } catch (err: any) {
        setDropError(err?.body?.error ?? err?.message ?? 'Upload failed.');
        setTimeout(() => setDropError(null), 4000);
        return;
      }
    }
    if (payloads.length === 0) return;

    try {
      const fresh = await getDecryptedDocument(target.id, vaultKey);
      if (!fresh) throw new Error('Page not found.');
      let markdown = fresh.markdown;
      for (const p of payloads) markdown = appendRef(markdown, buildAttachmentRef(p));
      await upsertDocument(
        target.id,
        fresh.customerId,
        fresh.parentId,
        {
          title: fresh.title,
          markdown,
          tags: fresh.tags,
          icon: fresh.icon,
          color: fresh.color,
        },
        vaultKey,
        false,
      );
      window.dispatchEvent(
        new CustomEvent(DOC_UPDATED_EVENT, { detail: { docId: target.id } }),
      );
    } catch (err: any) {
      setDropError(err?.body?.error ?? err?.message ?? 'Drop failed.');
      setTimeout(() => setDropError(null), 4000);
    }
  }

  function handleDragOver(targetId: string, e: React.DragEvent) {
    const accept =
      e.dataTransfer.types.includes(ATTACHMENT_DRAG_MIME) || dragHasFiles(e);
    if (!accept) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropTarget(targetId);
  }

  const contentSignature = docs.map((d) => d.title).join('');
  return (
    <ResizableAside
      storageKey="vault.col.pages"
      min={200}
      max={520}
      defaultWidth={288}
      enabled={breakpoint !== 'mobile'}
      contentSignature={contentSignature}
      className="border-r border-ink-800 bg-ink-900/60 flex flex-col min-h-0"
      fallbackClassName="w-full border-r border-ink-800 bg-ink-900/60 flex flex-col min-h-0"
    >
      {({ measureRef, rowAttr, labelAttr }) => (
        <>
          {/* Pages section — now fills the whole sidebar. */}
          <section className="flex flex-col min-h-0 flex-1">
            <div className="flex items-center justify-between px-3 py-2 border-b border-ink-800">
              <span className="text-xs uppercase tracking-wider text-ink-400">Pages</span>
              {customerId && canEdit && (
                <button className="btn-ghost !p-1" title="Add page" onClick={addNew}>
                  <Plus size={14} />
                </button>
              )}
            </div>
            <ul
              ref={measureRef as React.Ref<HTMLUListElement>}
              className="flex-1 overflow-y-auto"
            >
              {!customerId && (
                <li className="p-4 text-xs text-ink-500">Select a customer to see its pages.</li>
              )}
              {customerId && docs.length === 0 && (
                <li className="p-4 text-xs text-ink-500">No pages yet. Click + to create one.</li>
              )}
              {docs.map((d) => (
                <PageRow
                  key={d.id}
                  d={d}
                  selected={selectedDocId === d.id}
                  isEditing={editing === d.id}
                  isDropOver={dropTarget === d.id}
                  draftTitle={draftTitle}
                  draftIcon={draftIcon}
                  regenerating={regenerating}
                  showKeyInput={showKeyInput}
                  keyInput={keyInput}
                  rowAttr={rowAttr}
                  labelAttr={labelAttr}
                  setDraftTitle={setDraftTitle}
                  toggleKeyInput={() => setShowKeyInput((v) => !v)}
                  setKeyInput={setKeyInput}
                  applyKeyInput={applyKeyInput}
                  regenerateIcon={regenerateIcon}
                  beginEdit={() => {
                    setEditing(d.id);
                    setDraftTitle(d.title);
                    setDraftIcon(d.icon ?? DEFAULT_APP_ICON);
                    setEditBaselineTitle(d.title);
                    resetEditSessionState();
                  }}
                  cancelEdit={() => setEditing(null)}
                  saveEdit={() => saveEdit(d)}
                  remove={() => remove(d.id)}
                  onSelect={() => onSelect(d.id)}
                  onDragOver={(e) => handleDragOver(d.id, e)}
                  onDragLeave={() => setDropTarget((cur) => (cur === d.id ? null : cur))}
                  onDrop={(e) => handleDrop(d, e)}
                />
              ))}
            </ul>
            {dropError && (
              <div className="border-t border-red-900 bg-red-950/40 px-3 py-1.5 text-[10px] text-red-300">
                {dropError}
              </div>
            )}
          </section>

          {customerId && onOpenAttachments && (
            <button
              onClick={onOpenAttachments}
              className="flex items-center gap-2 px-3 py-2 border-t border-ink-800 text-xs text-ink-300 hover:bg-ink-800/60"
              title="View all attachments for this customer"
            >
              <Paperclip size={14} className="text-dragon-500" />
              <span>Attachments</span>
            </button>
          )}
        </>
      )}
    </ResizableAside>
  );
}

/**
 * Renders one page in the sidebar. Reads the live Y.Doc meta (when present)
 * so title/icon changes on the currently-open page show up instantly without
 * waiting for a snapshot round-trip.
 */
function PageRow({
  d,
  selected,
  isEditing,
  isDropOver,
  draftTitle,
  draftIcon,
  regenerating,
  showKeyInput,
  keyInput,
  rowAttr,
  labelAttr,
  setDraftTitle,
  toggleKeyInput,
  setKeyInput,
  applyKeyInput,
  regenerateIcon,
  beginEdit,
  cancelEdit,
  saveEdit,
  remove,
  onSelect,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  d: DecryptedDocument;
  selected: boolean;
  isEditing: boolean;
  isDropOver: boolean;
  draftTitle: string;
  draftIcon: string;
  regenerating: boolean;
  showKeyInput: boolean;
  keyInput: string;
  rowAttr: Record<string, string>;
  labelAttr: Record<string, string>;
  setDraftTitle: (v: string) => void;
  toggleKeyInput: () => void;
  setKeyInput: (v: string) => void;
  applyKeyInput: () => void;
  regenerateIcon: () => void;
  beginEdit: () => void;
  cancelEdit: () => void;
  saveEdit: () => void;
  remove: () => void;
  onSelect: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  const live = useLiveMeta(d.id);
  const canEdit = useCanEdit();
  const title = (live?.title ?? d.title) || 'Untitled';
  const iconKey = live?.icon ?? d.icon;
  const Icon = getAppIcon(iconKey);
  const DraftIcon = getAppIcon(draftIcon);

  return (
    <li
      {...rowAttr}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`group relative px-3 py-2 cursor-pointer border-l-2 ${
        isDropOver
          ? 'bg-dragon-500/10 border-dragon-500 ring-1 ring-inset ring-dragon-500/50'
          : selected
            ? 'bg-ink-800 border-dragon-500'
            : 'border-transparent hover:bg-ink-800/60'
      }`}
      onClick={() => !isEditing && onSelect()}
    >
      {isEditing ? (
        <div
          className="flex flex-col gap-2 py-1"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1.5">
            <div
              className="h-7 w-7 flex-shrink-0 flex items-center justify-center rounded border border-ink-700 bg-ink-800"
              title={draftIcon}
            >
              <DraftIcon size={14} className="text-dragon-500" />
            </div>
            <button
              type="button"
              className="btn-ghost !p-1"
              title="Regenerate icon (avoid last 5)"
              onClick={regenerateIcon}
              disabled={regenerating || !draftTitle.trim()}
            >
              <RefreshCw
                size={12}
                className={regenerating ? 'animate-spin' : ''}
              />
            </button>
            <button
              type="button"
              className="btn-ghost !p-1"
              title="Set icon key manually"
              onClick={toggleKeyInput}
            >
              <Pencil size={12} />
            </button>
            <input
              className="input !py-1 !text-sm flex-1 min-w-0"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveEdit();
                if (e.key === 'Escape') cancelEdit();
              }}
            />
          </div>
          {showKeyInput && (
            <input
              className="input !py-1 !text-xs w-full font-mono"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="icon key (e.g. shield-check)"
              onBlur={applyKeyInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyKeyInput();
                }
                if (e.key === 'Escape') {
                  toggleKeyInput();
                  setKeyInput('');
                }
              }}
            />
          )}
          <div className="flex justify-end gap-1">
            <button
              type="button"
              className="btn-ghost !p-1"
              onClick={cancelEdit}
              aria-label="Cancel"
            >
              <X size={14} />
            </button>
            <button
              type="button"
              className="btn-primary !px-2 !py-1"
              onClick={saveEdit}
              aria-label="Save"
            >
              <Check size={14} />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-ink-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div {...labelAttr} className="text-sm truncate">
              {title}
            </div>
            <div className="text-[10px] text-ink-500">
              {new Date(d.updatedAt).toLocaleDateString()}
            </div>
          </div>
          <div
            className={`absolute inset-y-0 right-0 flex items-center gap-1 pl-8 pr-2 transition-opacity bg-gradient-to-l to-transparent ${
              selected
                ? 'opacity-100 from-ink-800 via-ink-800'
                : 'opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto from-ink-900/95 via-ink-900/95'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {d.pendingSync && <span className="text-[10px] text-amber-400">●</span>}
            {canEdit && (
              <>
                <button
                  className="btn-ghost !p-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    beginEdit();
                  }}
                  title="Rename / change icon"
                >
                  <Edit2 size={12} />
                </button>
                <button
                  className="btn-ghost !p-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove();
                  }}
                  title="Delete"
                >
                  <Trash2 size={12} />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

function appendRef(existing: string, ref: string): string {
  if (!existing.trim()) return ref;
  const sep = existing.endsWith('\n') ? (existing.endsWith('\n\n') ? '' : '\n') : '\n\n';
  return existing + sep + ref;
}
